import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| TYPES ||============================== //

interface MetricValue {
  current: number;
  previous: number;
  changePercentage: number;
}

interface SummaryData {
  prSuccessRate: MetricValue;
  tasksCompleted: MetricValue;
  acuUsage: MetricValue;
}

interface ACUOverTime {
  date: string;
  acuUsed: number;
}

interface CostsData {
  acuOverTime: ACUOverTime[];
  acuPerMergedPR?: number;
}

interface PRMergeRateTrend {
  date: string;
  totalPrs: number;
  mergedPrs: number;
  mergeRate: number;
}

interface AgentActivity {
  currentlyProcessing: number;
  inQueue: number;
  lastCompletedMinutesAgo: number | null;
  avgProcessingTimeMinutes: number | null;
}

interface TopPerformingRepository {
  repositoryId: string;
  repositoryName: string;
  owner: string;
  totalPrs: number;
  mergedPrs: number;
  mergeRate: number;
  directRepoPRs?: number;
  projectWorkPRs?: number;
  directRepoPercentage?: number;
  projectWorkPercentage?: number;
}

interface ReviewBreakdown {
  totalReviews: number;
  aiPrReviews: number;
  humanPrReviews: number;
  childPrsCreated: number;
  childPrsMerged: number;
  totalCost: number;
  totalACU: number;
  avgCostPerReview: number;
}

interface PerformanceData {
  prMergeRateTrends: PRMergeRateTrend[];
  agentActivity: AgentActivity;
  topPerformingRepositories: TopPerformingRepository[];
  avgTimeToMerge?: number;
  reviewBreakdown?: ReviewBreakdown;
}

interface JiraAsanaWorkerPerformance {
  completed: number;
  rejected: number;
  successRate: number;
  totalACU: number;
  avgACUPerTask: number;
  trend: number;
}

interface PRReviewWorkerPerformance {
  completed: number;
  rejected: number;
  successRate: number;
  totalACU: number;
  avgACUPerReview: number;
  trend: number;
}

interface InsightsWorkerPerformance {
  completed: number;
  byType: { progressVelocity: number; codeQuality: number; securityPosture: number };
  byAudience: { technical: number; business: number };
  totalACU: number;
  avgACUPerReport: number;
  trend: number;
}

interface ChatWorkerPerformance {
  totalSessions: number;
  ticketsCreated: number;
  totalACU: number;
  avgACUPerSession: number;
  trend: number;
}

interface WorkerPerformanceMap {
  jira: JiraAsanaWorkerPerformance;
  asana: JiraAsanaWorkerPerformance;
  prReview: PRReviewWorkerPerformance;
  insights: InsightsWorkerPerformance;
  chat: ChatWorkerPerformance;
}

interface DashboardData {
  // Summary metrics
  totalACU: number;
  previousTotalACU: number;
  acuChangePercentage: number;
  prSuccessRate: number;
  previousPRSuccessRate: number;
  prRateChangePercentage: number;
  tasksCompleted: number;
  previousTasksCompleted: number;
  tasksChangePercentage: number;
  tasksInProgress: number;

  // Breakdowns
  acuBreakdown: {
    prReviewAgent: number;
    projects: number;
    prReviewAgentKnowledgeBase: number;
    projectsKnowledgeBase: number;
    prReviewAgentWorkers: number;
    projectsWorkers: number;
  };

  // Charts
  acuOverTime: Array<{ date: string; acuUsed: number }>;
  prMergeRateTrends: Array<{ date: string; totalPrs: number; mergedPrs: number; mergeRate: number }>;

  // Top consumers
  topRepositories: Array<{
    repositoryId: string;
    repositoryName: string;
    owner: string;
    totalACU: number;
    prReviewACU: number;
    projectWorkACU: number;
  }>;
  topProjects: Array<{
    projectId: string;
    projectName: string;
    totalACU: number;
    knowledgeBaseACU: number;
    workersACU: number;
  }>;

  // Agent activity
  agentActivity: {
    currentlyProcessing: number;
    inQueue: number;
    lastCompletedMinutesAgo: number | null;
    avgProcessingTimeMinutes: number | null;
  };

  // Knowledge base
  knowledgeBase: {
    totalEmbeddedPages: number;
    totalSources: number;
    totalACU: number;
    activeProjects: number;
  };

  // Worker performance
  workerPerformance: WorkerPerformanceMap;

  // Metadata
  dateRange: string;
  startDate: string;
  endDate: string;
  organizationId: string;
  generatedAt: string;
}

interface BillingSummary {
  acuUsed: number;
  acuRemaining: number;
  acuTotal: number;
  acuUsagePercentage: number;
  currentBalance: number;
  planAllocation: number;
  daysUntilDepletion: number | null;
  billingPeriodStart: string;
  billingPeriodEnd: string;
}

interface PRReviewBreakdown {
  totalReviews: number;
  aiPrReviews: number;
  humanPrReviews: number;
  childPrsCreated: number;
  childPrsMerged: number;
  totalACU: number;
  avgACUPerReview: number;
  avgACUPerAIReview: number;
  avgACUPerHumanReview: number;
  aiACU?: number;
  humanACU?: number;
  kbACU?: number;
}

interface RepositoryAnalyticsData {
  reviewBreakdown: PRReviewBreakdown;
  totalACU: number;
  totalPRsCreated: number;
  totalPRsMerged: number;
  prMergeRate: number;
  dateRange: string;
  startDate: string;
  endDate: string;
  repositoryId: string;
  generatedAt: string;
}

interface AnalyticsState {
  dashboard: DashboardData | null;
  projectAnalytics: DashboardData | null;  // Separate state for project analytics
  billingSummary: BillingSummary | null;
  repositoryAnalytics: RepositoryAnalyticsData | null;
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

// ==============================|| INITIAL STATE ||============================== //

const initialState: AnalyticsState = {
  dashboard: null,
  projectAnalytics: null,  // Initialize project analytics state
  billingSummary: null,
  repositoryAnalytics: null,
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

// New billing summary endpoint for settings page
export const getBillingSummary = createAsyncThunk(
  'analytics/getBillingSummary',
  async (
    params: {
      organizationId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiRequest(`/analytics/billing-summary?organizationId=${params.organizationId}`);
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// New consolidated dashboard endpoint
export const getDashboard = createAsyncThunk(
  'analytics/getDashboard',
  async (
    params: {
      organizationId?: string;
      projectId?: string;
      dateRange?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.organizationId) queryParams.append('organizationId', params.organizationId);
      if (params.projectId) queryParams.append('projectId', params.projectId);
      if (params.dateRange) queryParams.append('dateRange', params.dateRange);

      const response = await apiRequest(`/analytics/dashboard?${queryParams.toString()}`);
      // Unwrap the response from the API interceptor
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Get repository-specific analytics
export const getRepositoryAnalytics = createAsyncThunk(
  'analytics/getRepositoryAnalytics',
  async (
    params: {
      repositoryId: string;
      dateRange?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('repositoryId', params.repositoryId);
      if (params.dateRange) queryParams.append('dateRange', params.dateRange);

      const response = await apiRequest(`/analytics/repository?${queryParams.toString()}`);
      // Unwrap the response from the API interceptor
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Get project-specific analytics (uses current billing period by default)
// SECURITY: organizationId now extracted from user context on backend
export const getProjectAnalytics = createAsyncThunk(
  'analytics/getProjectAnalytics',
  async (
    params: {
      projectId: string;
      dateRange?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('projectId', params.projectId);
      // Default to current_period if no dateRange specified
      queryParams.append('dateRange', params.dateRange || 'current_period');

      const response = await apiRequest(`/analytics/project?${queryParams.toString()}`);
      // Unwrap the response from the API interceptor
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| SLICE ||============================== //

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
    clearAnalyticsState(state) {
      state.dashboard = null;
      state.projectAnalytics = null;
      state.billingSummary = null;
      state.repositoryAnalytics = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    // Get Billing Summary
    builder.addCase(getBillingSummary.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(getBillingSummary.fulfilled, (state, action) => {
      state.status = 'success';
      state.billingSummary = action.payload;
      state.error = '';
    });
    builder.addCase(getBillingSummary.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload as string;
    });

    // Get Dashboard (Consolidated)
    builder.addCase(getDashboard.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(getDashboard.fulfilled, (state, action) => {
      state.status = 'success';
      state.dashboard = action.payload;
      state.error = '';
    });
    builder.addCase(getDashboard.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload as string;
    });

    // Get Repository Analytics
    builder.addCase(getRepositoryAnalytics.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(getRepositoryAnalytics.fulfilled, (state, action) => {
      state.status = 'success';
      state.repositoryAnalytics = action.payload;
      state.error = '';
    });
    builder.addCase(getRepositoryAnalytics.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload as string;
    });

    // Get Project Analytics
    builder.addCase(getProjectAnalytics.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(getProjectAnalytics.fulfilled, (state, action) => {
      state.status = 'success';
      // Store project analytics in its own separate field
      state.projectAnalytics = action.payload;
      state.error = '';
    });
    builder.addCase(getProjectAnalytics.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload as string;
    });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearError, clearAnalyticsState } = analyticsSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectDashboard = (state: any) => state.analytics.dashboard;
export const selectProjectAnalytics = (state: any) => state.analytics.projectAnalytics;
export const selectBillingSummary = (state: any) => state.analytics.billingSummary;
export const selectRepositoryAnalytics = (state: any) => state.analytics.repositoryAnalytics;
export const selectAnalyticsStatus = (state: any) => state.analytics.status;
export const selectAnalyticsError = (state: any) => state.analytics.error;

// ==============================|| REDUCER ||============================== //

export default analyticsSlice.reducer;
